import countryLinks from '../data/countryLinks.json';
import guideLinks from '../data/guideLinks.json';
import { HOME_FAQS } from '../data/seoFaqs';
import { withSlash } from '../seo/urls';
import SeoFaqItems from './SeoFaqItems';
import React, { Suspense, lazy, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { SlashLink as Link } from './SlashLink';
import { TOPICS } from '../data/topics';
import { LEARNING_MODULE_COUNT, OFFICIAL_OUTLINES_URL, SYLLABUS_YEAR } from '../data/syllabus';
import {
  EXAM_DAY_CLARITY,
  QUESTION_BANK_LABEL,
  QUESTION_BANK_SHORT,
  SESSION_DURATION_LABEL,
  SESSION_DURATION_MINUTES,
  SESSION_MOCK_SHORT,
} from '../constants/product';
import {
  ANCHOR_PRICE_USD,
  FOUNDING_OFFER_END_LABEL,
  foundingOfferPublicLine,
  foundingTierStepLine,
  freeMockPublicLine,
  getDynamicPricing,
  planPricingPublicLine,
  refundPolicyShortLine,
  unlockMocksMonthlyLeadCta,
  lifetimeCtaPriceUsd,
} from '../constants/pricing';
import FoundingOfferUrgency from './FoundingOfferUrgency';
import LevelSwitcher from './LevelSwitcher';
import DeferredBoot from './DeferredBoot';
import { HOME_FEATURED_POSTS } from '../data/blogFeatured';
import { applyHomeSeo } from '../utils/blogSeo';
import useSiteSettings from '../hooks/useSiteSettings';
import useAccess from '../hooks/useAccess';
import SiteFooter from './SiteFooter';
import {
  examDatesBlogPath,
  formatExamWindowRange,
  getNextExamWindow,
  nextExamWindowPublicLine,
} from '../constants/examWindows';
import '../styles/home.css';
import ArrowIcon from './ArrowIcon';
import HeroSessionClock from './HeroSessionClock';
import ContinueMockCard from './ContinueMockCard';
import usePricingTier from '../hooks/usePricingTier';

const SocialProof = lazy(() => import('./SocialProof'));

const SPEC_ICON_PATHS = {
  doc: 'M6 3h8l3 3v11H6zM9 8h5M9 11h5M9 14h3',
  list: 'M4 6h1M4 10h1M4 14h1M8 6h9M8 10h9M8 14h9',
  bars: 'M5 16v-4M10 16V8M15 16V4',
  clock: 'M10 3.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM10 6.5V10l2.5 1.5',
  info: 'M10 3.5a6.5 6.5 0 1 0 0 13 6.5 6.5 0 0 0 0-13zM10 9v4.5M10 6.5v.5',
};

const SpecIcon = ({ name }) => (
  <svg className="spec-icon" viewBox="0 0 20 20" width="24" height="24" aria-hidden="true" focusable="false">
    <path d={SPEC_ICON_PATHS[name]} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Home = () => {
  usePricingTier(); // re-render CTA copy when the regional sale resolves / ends
  const { settings, loading: settingsLoading } = useSiteSettings();
  const access = useAccess();
  // Prefer live-offer copy while settings load (DEFAULT_FLAGS already bootstraps monetization on).
  const paidLive = settingsLoading ? true : !!settings.monetizationEnabled;
  const topicDrillsUnlocked = !paidLive || access.paid || access.isAdmin;
  const foundingActive = getDynamicPricing().active;
  const offerLine = foundingActive ? foundingOfferPublicLine() : planPricingPublicLine();
  const priceUsd = lifetimeCtaPriceUsd();
  const trialExhausted = paidLive && access.freeUsed && !access.paid;
  const primaryMockLabel = access.user ? 'Start Free Mock Exam' : 'Start free Mock Exam 1 — no account needed';
  const tierStepLine = foundingTierStepLine();
  const freeMockHref = '/quiz/session-1';
  const featuredPosts = HOME_FEATURED_POSTS;
  const nextL1 = getNextExamWindow('l1');
  const nextL2 = getNextExamWindow('l2');
  const nextL3 = getNextExamWindow('l3');
  const l1DateLine = nextExamWindowPublicLine('l1');


  useEffect(() => applyHomeSeo(), []);

  if (!access.loading && access.user && (access.paid || access.paidL2 || access.paidL3 || access.hasPackage || access.isAdmin)) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="page-shell home-v2">
      <main className="page-shell-main">
        <ContinueMockCard uid={access.user?.uid || ''} placement="home" />
        <section className="home-hero">
          <div className="home-hero-inner">
            <div>
              <h1>
                <span className="home-hero-accent">Free</span> CFA Level 1 Mock Exam
              </h1>
              <p className="lede">
                Test your readiness with 90 questions in a {SESSION_DURATION_MINUTES}-minute timed
                session. Get your score for free.
              </p>
              <div className="hero-actions">
                {trialExhausted ? (
                  <>
                    <Link className="btn-primary" to="/pricing#purchase">
                      {unlockMocksMonthlyLeadCta()}
                      <ArrowIcon />
                    </Link>
                    <Link className="hero-text-link" to={freeMockHref}>
                      Try free (score only)
                    </Link>
                    {tierStepLine ? (
                      <p className="hero-tier-note">{tierStepLine}</p>
                    ) : null}
                  </>
                ) : (
                  <>
                    <Link className="btn-primary" to={freeMockHref}>
                      {primaryMockLabel}
                      <ArrowIcon />
                    </Link>
                    {paidLive ? (
                      <Link className="hero-text-link" to="/pricing#purchase">
                        Lifetime access
                      </Link>
                    ) : (
                      <Link className="hero-text-link" to="/study-guide">
                        Study guide
                      </Link>
                    )}
                  </>
                )}
              </div>
              {paidLive && !trialExhausted ? (
                <p className="hero-fine-print">
                  {freeMockPublicLine()} — explanations and analysis unlock with lifetime access.
                </p>
              ) : null}
              {paidLive ? (
                <FoundingOfferUrgency className="home-hero-offer" variant="hero" />
              ) : null}
            </div>

            <aside className="hero-spec" aria-label="Your timed session">
              <h2>Your timed session</h2>
              <HeroSessionClock minutes={SESSION_DURATION_MINUTES} ctaHref={freeMockHref} />
              <ul className="hero-spec-list">
                <li>
                  <SpecIcon name="doc" />
                  90 multiple-choice questions
                </li>
                <li>
                  <SpecIcon name="list" />
                  3 answer choices per question (A, B, C)
                </li>
                <li>
                  <SpecIcon name="bars" />
                  Score included for free
                </li>
              </ul>
              <p className="spec-note">
                <SpecIcon name="info" />
                <span>{EXAM_DAY_CLARITY}</span>
              </p>
            </aside>
          </div>
        </section>

        <section className="home-section home-focus-section" aria-label="A focused practice session">
          <h2>A focused practice session</h2>
          <ul className="home-focus-steps">
            <li>
              <SpecIcon name="clock" />
              <span>Practice under timed conditions</span>
            </li>
            <li>
              <SpecIcon name="doc" />
              <span>Check your score</span>
            </li>
            <li>
              <SpecIcon name="bars" />
              <span>{paidLive ? 'Review every miss with lifetime access' : 'Review every miss'}</span>
            </li>
          </ul>
          <div className="home-plan-row">
            <aside className="home-plan-card" aria-label="Upcoming CFA exam windows">
              <h3>Upcoming exam windows</h3>
              <ul className="home-window-list">
                {[
                  { level: 'l1', label: 'Level 1', window: nextL1 },
                  { level: 'l2', label: 'Level 2', window: nextL2 },
                  { level: 'l3', label: 'Level 3', window: nextL3 },
                ].map((row) => (
                  <li key={row.level}>
                    <span className="home-window-level">{row.label}</span>
                    <strong>
                      {formatExamWindowRange(row.window)}
                      {row.level === 'l1' && l1DateLine.includes('last 2026') ? (
                        <span className="home-window-tag">Last of 2026</span>
                      ) : null}
                    </strong>
                    <Link to={examDatesBlogPath(row.level)}>
                      {row.label} dates
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
            <aside className="home-plan-card" aria-label="Choose your exam level">
              <h3>Choose your level</h3>
              <LevelSwitcher className="home-level-switcher" />
              <p>
                Each level has its own free Mock 1. Lifetime access is separate per level.
              </p>
              <p className="home-plan-links">
                <Link to="/level/2">Level 2 mock exams</Link>
                <Link to="/level/3">Level 3 mock exams</Link>
              </p>
            </aside>
          </div>
        </section>

        <section className="home-section home-highlights-section" aria-label="Exam highlights">
          <h2>Built like the real thing</h2>
          <p className="section-lede">
            Every mock follows the official session structure, so nothing on exam day is new.
          </p>
          <div className="hero-highlights">
            <article className="hero-highlight">
              <strong>8</strong>
              <b>Full mocks</b>
              <span>Named timed sessions at official topic weights</span>
            </article>
            <article className="hero-highlight">
              <strong>{QUESTION_BANK_LABEL}</strong>
              <b>Original items</b>
              <span>Practice questions across every Level 1 topic</span>
            </article>
            <article className="hero-highlight">
              <strong>{TOPICS.length}</strong>
              <b>Level 1 topics</b>
              <span>Full {SYLLABUS_YEAR} curriculum coverage, from Ethics to Portfolio Management</span>
            </article>
          </div>

          <div className="home-session-details" id="session-details">
            <div>
              <h3>Session details</h3>
              <p>
                Practice a full timed session under computer-based testing conditions: 90 multiple-choice
                items, {SESSION_DURATION_LABEL}, three answer choices (A, B, C). Topic allocation follows
                the {SYLLABUS_YEAR} Level 1 topic weights — the same structure described on the{' '}
                <a href={OFFICIAL_OUTLINES_URL} target="_blank" rel="noreferrer">
                  official Level 1 exam page
                </a>
                .
                {paidLive
                  ? ` ${freeMockPublicLine()}. Explanations, topic breakdown, eight weighted mocks (no shared items across forms), post-mock analysis, and all topic drills from a ${QUESTION_BANK_LABEL} bank unlock with lifetime access.`
                  : ` Drawn from a ${QUESTION_BANK_SHORT} bank. A free account is required so your sitting and progress can be saved; no payment is required.`}
              </p>
            </div>
            <dl className="spec-table" aria-label="Session specifications">
              <div>
                <dt>Items</dt>
                <dd>90 multiple-choice</dd>
              </div>
              <div>
                <dt>Time allowed</dt>
                <dd>{SESSION_DURATION_LABEL}</dd>
              </div>
              <div>
                <dt>Choices per item</dt>
                <dd>Three (A, B, C)</dd>
              </div>
              <div>
                <dt>Topic weights</dt>
                <dd>{SYLLABUS_YEAR} Level 1 outline</dd>
              </div>
              <div>
                <dt>Delivery</dt>
                <dd>Computer-based (CBT)</dd>
              </div>
              <div>
                <dt>Calculator</dt>
                <dd>On-screen TI BA II Plus (practice tool)</dd>
              </div>
              <div>
                <dt>Result</dt>
                <dd>Score free · topic breakdown &amp; analysis with paid access</dd>
              </div>
            </dl>
          </div>
        </section>

        <section className="home-exam-band" id="proof" aria-label="Screens from the actual mock exam">
          <div className="home-exam-band-inner">
            <div className="home-exam-band-copy">
              <span className="home-exam-band-tag">Exam mode</span>
              <h2>What you actually sit</h2>
              <p>
                Real screens from this site — not mockups. A Prometric-style CBT UI and timer modeled
                on exam day, starting with the free Mock 1.
              </p>
              <Link className="btn-primary invert" to={trialExhausted ? '/pricing#purchase' : freeMockHref}>
                {trialExhausted ? unlockMocksMonthlyLeadCta() : primaryMockLabel}
              </Link>
            </div>
            <figure className="proof-strip-item">
              <img
                src="/assets/images/proof/exam-question-review.png"
                alt="CFA Mock Exam question screen showing a Level 1 item, the section timer, flag control, and the Section Review grid with flagged and attempted items"
                loading="lazy"
                width={1600}
                height={1049}
              />
              <figcaption>
                Prometric-style CBT: 90-item navigation, flag &amp; Section Review grid, on-screen BA
                II Plus, and a live section timer — so exam day has no surprises.
              </figcaption>
            </figure>
            <figure className="proof-strip-item proof-strip-item-report">
              <img
                src="/assets/images/proof/score-topic-bars.png"
                alt="CFA Mock Exam score report showing a 72.2% score and a bar chart breakdown across the ten Level 1 topic areas, with weaker topics highlighted"
                loading="lazy"
                width={2080}
                height={1014}
              />
              <figcaption>
                Paid score report: topic breakdown across all 10 Level 1 topics, right after each
                sitting. Free Mock 1 shows your overall score.
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="home-section" id="how">
          <h2>How to use this mock</h2>
          <p className="section-lede">
            Treat it as a dress rehearsal. Full clock, review only after submission. If life interrupts, close the page: your clock pauses and you can continue within two days.
          </p>
          <ol className="procedure-list">
            <li>
              <strong>Sit the session.</strong> Answer 90 items in {SESSION_DURATION_MINUTES} minutes.
              Navigate with Back / Next, flag items for review, open the on-screen BA II Plus from the
              Calculator button, and open Section Review as needed.
            </li>
            <li>
              <strong>Finish the section.</strong> Confirm when ready. Incomplete items are scored as
              incorrect — same rule as the computer-based exam.
            </li>
            <li>
              <strong>Review the report.</strong> Study every miss. With lifetime access, use the
              weak-topic analysis and timing tells, then drill and sit another full mock before your
              appointment window.
            </li>
          </ol>
        </section>

        <section className="home-section" id="topics">
          <h2>Topic practice</h2>
          <p className="section-lede">
            {topicDrillsUnlocked
              ? paidLive
                ? `${freeMockPublicLine()}. Topic drills and Mocks 2–8 unlock with lifetime access.`
                : 'Untimed drills by Level 1 topic. The timed session remains the closest simulation of exam day.'
              : `${freeMockPublicLine()} first, or purchase lifetime access for unlimited topic drills and all eight mocks.`}
          </p>
          <ul className="topic-table">
            {TOPICS.map((topic) => (
              <li key={topic.slug}>
                <Link
                  className="topic-row"
                  to={topicDrillsUnlocked ? `/quiz/${topic.slug}` : '/pricing#purchase'}
                >
                  <span className="topic-row-label">
                    <strong>{topic.label}</strong>
                    <em>
                      {topic.weightLabel} · Session {topic.session}
                    </em>
                  </span>
                  <span className="topic-row-go">{topicDrillsUnlocked ? 'Practice' : 'Unlock'}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="home-section" id="resources">
          <h2>Study resources</h2>
          <p className="section-lede">
            Free Level 1 prep beyond the timed session — study guide and formula glossary.
            {paidLive ? ' Topic drills and the other mocks unlock with lifetime access.' : ''}
          </p>
          <p><Link to="/cfa-mock-exams/">See all CFA mock exams</Link></p>
          <ul className="home-resource-links">
            <li>
              <Link to={freeMockHref}>
                {primaryMockLabel}
              </Link>
              {' '}
              — full timed Mock 1, score only ·{' '}
              <Link to="/pricing#purchase">Unlock Mocks 2–8</Link>
            </li>
            <li>
              <Link to="/study-guide">Level 1 study guide</Link> — one lesson per {LEARNING_MODULE_COUNT}{' '}
              learning modules ({SYLLABUS_YEAR})
            </li>
            <li>
              <Link to="/glossary?formulas=1">Formula glossary</Link> — searchable Level 1 formulas
            </li>
            <li>
              <Link to={examDatesBlogPath('l1')}>Level 1 exam dates</Link> — next window{' '}
              {formatExamWindowRange(nextL1)}
              {' · '}
              <Link to={examDatesBlogPath('l2')}>Level 2</Link> /{' '}
              <Link to={examDatesBlogPath('l3')}>Level 3</Link> calendars
            </li>
            <li>
              <Link to="/blog/free-cfa-level-1-mock-exam-2026">Free CFA Level 1 practice exam</Link> — sit Mock
              1, then unlock Mocks 2–8
            </li>
            <li>
              <Link to="/blog">Study blog</Link> — pass rate, formulas, ethics, and study plans
            </li>
            {paidLive ? (
              <li>
                <Link to="/pricing">Lifetime or monthly access</Link> — eight full timed mocks, analysis, and topic drills
              </li>
            ) : null}
          </ul>
          {featuredPosts.length ? (
            <div className="home-blog-featured">
              <h3>Popular guides</h3>
              <ul>
                {featuredPosts.map((post) => (
                  <li key={post.slug}>
                    <Link to={post.path || `/blog/${post.slug}`}>{post.title}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </section>

        {paidLive ? (
          <DeferredBoot timeoutMs={1800}>
            <Suspense fallback={null}>
              <section className="home-section home-social-proof">
                <SocialProof showGoals={!trialExhausted} />
              </section>
            </Suspense>
          </DeferredBoot>
        ) : null}

        <section className="home-section" id="faq">
          <h2>Frequently asked questions</h2>
          <div className="faq-list">
            <SeoFaqItems faqs={HOME_FAQS} />
            <article className="faq-item">
              <h3>Curriculum coverage</h3>
              <p>
                The {SYLLABUS_YEAR} CFA® Level 1 outline — ten topics, {LEARNING_MODULE_COUNT} learning
                modules. Session 1: Ethics, Quantitative Methods, Economics, Financial Statement
                Analysis, Corporate Issuers. Session 2: Equity, Fixed Income, Derivatives, Alternatives,
                Portfolio Management. Financial reporting items use IFRS unless a question states US
                GAAP.
              </p>
            </article>
            <article className="faq-item">
              <h3>Affiliation</h3>
              <p>
                This is not an official CFA Institute mock. CFA® and Chartered Financial Analyst® are
                trademarks of CFA Institute. This site is independent practice software.
              </p>
            </article>
            <article className="faq-item">
              <h3>Question bank</h3>
              <p>
                Paid access includes eight named timed mocks (720 unique items at official{' '}
                {SYLLABUS_YEAR} topic weights), post-mock analysis of weak topics and timing, and
                drills drawn from our {QUESTION_BANK_SHORT.toLowerCase()} bank. Forms do not share
                stems. {EXAM_DAY_CLARITY}
              </p>
            </article>
            <article className="faq-item">
              <h3>Session length</h3>
              <p>{EXAM_DAY_CLARITY} Sit two of our mocks back-to-back if you want a full 180-item day.</p>
            </article>
            <article className="faq-item">
              <h3>Registration and checkout</h3>
              <p>
                {paidLive
                  ? `Start free Mock Exam 1 without an account. Progress stays on this browser; create a free account after finishing to save your report. Sign in before checkout so purchase access attaches to the same account. ${offerLine}`
                  : 'Create a free account before beginning so your sitting, score, and review history are saved.'}
              </p>
            </article>
            <article className="faq-item">
              <h3>Formulas and definitions</h3>
              <p>
                The <Link to="/glossary">glossary</Link> lists Level 1 definitions and{' '}
                <Link to="/glossary?formulas=1">formulas</Link> (CAPM, duration, WACC, put-call
                parity, and related), arranged A–Z. See also our{' '}
                <Link to="/blog/cfa-level-1-formulas-2026">Level 1 formulas guide</Link>.
              </p>
            </article>
            <article className="faq-item">
              <h3>Mock exam practice</h3>
              <p>
                Start the{' '}
                <Link to="/blog/free-cfa-level-1-mock-exam-2026">free CFA Level 1 mock exam</Link>, then
                read{' '}
                <Link to="/blog/cfa-level-1-practice-questions-free">
                  free CFA Level 1 sample questions
                </Link>{' '}
                and <Link to="/blog/how-to-pass-cfa-level-1-study-plan">build a 300-hour study plan</Link>{' '}
                before the{' '}
                <Link to={examDatesBlogPath('l1')}>{formatExamWindowRange(nextL1)} Level 1 window</Link>.
              </p>
            </article>
            <article className="faq-item">
              <h3>{foundingActive ? 'Founding pricing' : 'Pricing'}</h3>
              <p>
                {foundingActive
                  ? `${offerLine} ${freeMockPublicLine()} to start. After ${FOUNDING_OFFER_END_LABEL}, the standard lifetime rate is $${ANCHOR_PRICE_USD}.`
                  : `${planPricingPublicLine()} ${freeMockPublicLine()} to start.`}
              </p>
            </article>
            <article className="faq-item">
              <h3>Refunds</h3>
              <p>
                {refundPolicyShortLine()}. See our{' '}
                <Link to="/terms">Terms of Use</Link> for full details. Lifetime is a one-time
                payment; monthly plans auto-renew and can be canceled anytime.
              </p>
            </article>
            <article className="faq-item">
              <h3>Target score</h3>
              <p>
                CFA Institute does not publish a passing score. Many candidates treat approximately 70%
                on mocks as a working target — not a guarantee.
              </p>
            </article>
          </div>
        </section>

        <div className="home-bottom-cta">
          <div>
            <p className="eyebrow light">Ready when you are</p>
            <h2>{trialExhausted ? 'Unlock full access — or retake free Mock 1' : 'Begin a timed mock'}</h2>
            <p>
              {paidLive
                ? `${freeMockPublicLine()} · Lifetime or monthly access unlocks Mocks 2–8, explanations, and drills · ${offerLine}`
                : `${SESSION_MOCK_SHORT} · Free account required · ${QUESTION_BANK_LABEL} item bank`}
            </p>
          </div>
          <Link
            className="btn-primary invert"
            to={trialExhausted ? '/pricing#purchase' : freeMockHref}
          >
            {trialExhausted ? `Purchase $${priceUsd} lifetime` : access.user ? 'Choose a mock' : 'Start free Mock Exam 1 — no account needed'}
          </Link>
        </div>
        <section className="home-section"><nav aria-label="CFA Level 1 planning guides"><h2>CFA Level 1 planning guides</h2><ul>{guideLinks.map(({ path, name }) => <li key={path}><Link to={withSlash(path)}>{name}</Link></li>)}</ul></nav></section>
        <section className="home-section"><nav aria-label="CFA exam guides by country"><h2>CFA exam guides by country</h2><ul>{countryLinks.map(({ path, name }) => <li key={path}><Link to={withSlash(path)}>CFA exam guide: {name}</Link></li>)}</ul></nav></section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Home;
